import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod } from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface DiagnosisQueryParams {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export interface DiagnosisOverviewKpiData {
  patientsSeen: string
  patientsDiagnosed: string
  insuranceValue: string
  patientValue: string
  diagnosisCaptureRate: string
  diagnosisCaptureGoal: string
  primaryValue: number
  secondaryValue: number
}

export interface DiagnosisOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  goals: null
  kpis: DiagnosisOverviewKpiData
}

export interface DiagnosisDetailKpiData {
  patientsSeen: string
  patientsDiagnosed: string
  diagnosisCaptureRate: string
  diagnosisCaptureGoal: string
  insuranceValue: string
  patientValue: string
  comparisonLabel: string
  patientsSeenDelta: string
  patientsSeenTrend: 'up' | 'down'
  patientsDiagnosedDelta: string
  patientsDiagnosedTrend: 'up' | 'down'
  diagnosisCaptureDelta: string
  diagnosisCaptureTrend: 'up' | 'down'
  insuranceDelta: string
  insuranceTrend: 'up' | 'down'
  patientDelta: string
  patientTrend: 'up' | 'down'
}

export interface DiagnosisChartPoint {
  label: string
  value: number
  isCurrent?: boolean
}

export interface DiagnosisChartConfig {
  data: DiagnosisChartPoint[]
  yDomain: [number, number]
  yTicks: number[]
}

export interface DiagnosisVisitRow {
  id: string
  patientId: string
  patientName: string
  dateOfVisit: string
  provider: string
  diagnosisSummary: string
  treatmentValue: string
  payer: 'Insurance' | 'Patient'
}

export interface DiagnosisDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: DiagnosisDetailKpiData
  patientsSeenChart: DiagnosisChartConfig
  diagnosisCaptureChart: DiagnosisChartConfig
  recentVisits: DiagnosisVisitRow[]
}

const diagnosisGet = <T>(
  path: string,
  params: DiagnosisQueryParams,
  options?: { signal?: AbortSignal },
): Promise<T> =>
  axios
    .get<
      ApiResponse<T>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/treatment/diagnosis/${path}`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getDiagnosisOverview = (
  params: DiagnosisQueryParams,
  options?: { signal?: AbortSignal },
): Promise<DiagnosisOverviewResult> => diagnosisGet('overview', params, options)

export const getDiagnosisDetail = (
  params: DiagnosisQueryParams,
  options?: { signal?: AbortSignal },
): Promise<DiagnosisDetailResult> => diagnosisGet('detail', params, options)
